/**
 * Meta-only GTM isolation and consent-safe asynchronous startup.
 * GTM-WGFZFB8R v6 owns pixel init and the page_view/new_quote tags. The app
 * owns the SDK loader because the published bootstrap skips SDK injection
 * when fbq already exists. Google measurement stays on window.dataLayer.
 *
 * The app's standard fbq bootstrap starts REVOKED. The actual SDK is ready
 * before GTM is requested, so a withdrawal during GTM download reaches the
 * real SDK before the container can initialize its pixel. Curated events
 * wait locally for readiness; withdrawal discards every pending event.
 */

export const MARKETING_DATALAYER_NAME = "marketingDataLayer";
export const MARKETING_GTM_ID = "GTM-WGFZFB8R";
const SCRIPT_ID = "gtm-marketing-script";
const SDK_SCRIPT_ID = "meta-marketing-sdk";
const SDK_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const MAX_PENDING_EVENTS = 100;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    marketingDataLayer?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fbq?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _fbq?: any;
  }
}

type PendingEvent = {
  epoch: number;
  event: string;
  params: Record<string, string | number | boolean>;
};

let containerRequested = false;
let gtmRequested = false;
let containerReady = false;
let sdkReady = false;
let consentEpoch = 0;
let pendingEvents: PendingEvent[] = [];
// Component effects can repeat on query-only changes or remounts. Keep the
// final page acceptance per document/consent epoch, including pending startup.
let lastAcceptedPage: { path: string; epoch: number } | null = null;
let desiredFbqConsent: "grant" | "revoke" = "revoke";
let fbqConsentApplied: "grant" | "revoke" | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let fbqConsentTarget: any = null;
let sdkWatch: number | null = null;

function stopSdkWatch(): void {
  if (sdkWatch !== null && typeof window !== "undefined") window.clearInterval(sdkWatch);
  sdkWatch = null;
}

/** Test-only reset; DOM/vendor state is owned by the test fixture. */
export function __resetMarketingDataLayerForTests(): void {
  stopSdkWatch();
  containerRequested = false;
  gtmRequested = false;
  containerReady = false;
  sdkReady = false;
  consentEpoch = 0;
  pendingEvents = [];
  lastAcceptedPage = null;
  desiredFbqConsent = "revoke";
  fbqConsentApplied = null;
  fbqConsentTarget = null;
}

export function ensureMarketingDataLayer(): unknown[] | null {
  if (typeof window === "undefined") return null;
  window.marketingDataLayer = window.marketingDataLayer || [];
  return window.marketingDataLayer;
}

function currentlyAllowed(): boolean {
  if (desiredFbqConsent !== "grant") return false;
  if (typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return false;
  try {
    const optOut = localStorage.getItem("mb-ccpa-opt-out");
    if (optOut === "true" || optOut === "1") return false;
  } catch { /* The synchronous consent-state latch still applies. */ }
  return true;
}

/** Apply to the existing stub OR SDK before any init/event can follow. */
function applyFbqConsent(): boolean {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return false;
  const target = currentlyAllowed() ? "grant" : "revoke";
  if (fbqConsentTarget === window.fbq && fbqConsentApplied === target) return true;
  try {
    window.fbq("consent", target);
    fbqConsentTarget = window.fbq;
    fbqConsentApplied = target;
    return true;
  } catch {
    fbqConsentApplied = null;
    return false;
  }
}

/** Standard Meta bootstrap, with revoke as its FIRST command. No pixel init. */
function ensureFbqBootstrap(): void {
  if (typeof window.fbq === "function") {
    applyFbqConsent();
    return;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod.apply(n, args);
    else n.queue.push(args);
  };
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n("consent", "revoke");
  fbqConsentTarget = n;
  fbqConsentApplied = "revoke";
  applyFbqConsent();
}

function flushPendingEvents(): void {
  if (!containerReady || !sdkReady) return;
  if (!applyFbqConsent()) return;
  if (!currentlyAllowed()) {
    pendingEvents = [];
    lastAcceptedPage = null;
    return;
  }
  const pending = pendingEvents;
  pendingEvents = [];
  for (const entry of pending) {
    if (entry.epoch !== consentEpoch || !currentlyAllowed()) continue;
    ensureMarketingDataLayer()?.push({ event: entry.event, ...entry.params });
  }
}

function requestGtmWhenReady(): void {
  // Advanced Consent Mode: the container loads regardless of the advertising
  // choice; fbq consent (revoked by default) and event gating keep Meta
  // opt-in. `applyFbqConsent` re-asserts the current state before load.
  if (!containerRequested || !sdkReady) return;
  if (!applyFbqConsent()) return;
  if (gtmRequested) {
    flushPendingEvents();
    return;
  }
  gtmRequested = true;
  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  const s = existing || document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  if (!existing) {
    ensureMarketingDataLayer()?.push({ "gtm.start": Date.now(), event: "gtm.js" });
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(MARKETING_GTM_ID)}&l=${MARKETING_DATALAYER_NAME}`;
  }
  s.addEventListener("load", () => {
    // The SDK was real BEFORE this container was requested. Its consent has
    // therefore already received any mid-download withdrawal, including
    // before the container executes its unguarded init tag.
    applyFbqConsent();
    containerReady = true;
    flushPendingEvents();
  });
  s.addEventListener("error", () => { pendingEvents = []; });
  if (!existing) document.head.appendChild(s);
}

function observeSdkReady(): boolean {
  if (typeof window.fbq !== "function" || typeof window.fbq.callMethod !== "function") return false;
  sdkReady = true;
  stopSdkWatch();
  // Reapply after bootstrap processing; do not infer that download completion
  // means the visitor's earlier grant is still current.
  fbqConsentApplied = null;
  applyFbqConsent();
  if (!currentlyAllowed()) {
    pendingEvents = [];
    lastAcceptedPage = null;
  }
  requestGtmWhenReady();
  return true;
}

function ensureMetaSdk(): void {
  ensureFbqBootstrap();
  if (observeSdkReady()) return;
  const existing = document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null ||
    document.querySelector<HTMLScriptElement>('script[src*="connect.facebook.net/"][src*="/fbevents.js"]');
  const s = existing || document.createElement("script");
  if (!existing) {
    s.id = SDK_SCRIPT_ID;
    s.async = true;
    s.src = SDK_SRC;
  }
  s.addEventListener("load", () => { observeSdkReady(); });
  s.addEventListener("error", () => {
    stopSdkWatch();
    pendingEvents = [];
  });
  // Also handles an SDK script already inserted by another permitted owner.
  if (sdkWatch === null) {
    let ticks = 0;
    sdkWatch = window.setInterval(() => {
      if (observeSdkReady()) return;
      if (++ticks >= 100) stopSdkWatch();
    }, 100);
  }
  if (!existing) document.head.appendChild(s);
}

/** Synchronous, called BEFORE consent subscribers. */
export function setMarketingConsent(granted: boolean): void {
  const next = granted ? "grant" : "revoke";
  if (next !== desiredFbqConsent) consentEpoch += 1;
  desiredFbqConsent = next;
  if (!currentlyAllowed()) {
    pendingEvents = [];
    lastAcceptedPage = null;
  }
  applyFbqConsent();
  if (currentlyAllowed()) requestGtmWhenReady();
}

/** Actual readiness, not merely the presence of a downloading script. */
export function isMarketingContainerLoaded(): boolean {
  return containerReady && sdkReady;
}

/**
 * Start once at app startup (advanced Consent Mode). The SDK and container
 * load with fbq consent REVOKED unless Advertising is currently granted, so
 * Meta measurement stays opt-in: no Meta event is accepted or emitted while
 * revoked (`pushMarketingEvent`/`flushPendingEvents` enforce that).
 */
export function loadMarketingContainer(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  containerRequested = true;
  ensureMetaSdk();
  requestGtmWhenReady();
}

/**
 * Accept a curated, currently permitted event. Pending startup events are
 * bounded, local, and valid only for this uninterrupted consent epoch. A true
 * result means accepted (possibly waiting for readiness), not a network hit.
 */
export function pushMarketingEvent(
  event: string,
  params: Record<string, string | number | boolean> = {},
  allowed = false,
): boolean {
  if (typeof window === "undefined" || !allowed || !currentlyAllowed() || !containerRequested) return false;
  if (!applyFbqConsent()) return false;
  // Meta receives a public pathname, never a tracking query. Return true for
  // an already accepted page so callers consume it without adding a duplicate
  // to either the pending queue or GTM. A -> B -> A remains three page views.
  const pagePath = event === "page_view" && typeof params.page_path === "string"
    ? params.page_path.split(/[?#]/)[0] : null;
  if (pagePath && lastAcceptedPage?.path === pagePath && lastAcceptedPage.epoch === consentEpoch) return true;
  if (!(containerReady && sdkReady) && pendingEvents.length >= MAX_PENDING_EVENTS) return false;
  // Reserve before vendor execution, which can synchronously invoke callbacks.
  if (pagePath) lastAcceptedPage = { path: pagePath, epoch: consentEpoch };
  if (containerReady && sdkReady) {
    ensureMarketingDataLayer()?.push({ event, ...params });
    return true;
  }
  pendingEvents.push({ epoch: consentEpoch, event, params: { ...params } });
  return true;
}
